import { AgentV2WebSourceIcon } from '@/agent-v2/references/AgentV2WebSourceIcon'
import { cloneElement, useMemo, useRef, type FocusEvent, type MouseEvent, type ReactElement } from 'react'
import { Sparkles } from '@/assets/icons'
import { Link } from '@/components/link/Link'
import CustomizableTooltip from '@/components/ui/CustomizableTooltip'
import type { AgentV2ReferenceSectionSupport } from './agent-v2-reference-sections'
import { pickCitationAnchorRect } from './pick-citation-anchor-rect'

type WebSource = { url: string; title: string }

export function AgentV2WebSourceContent({ source, supports }: { source: WebSource; supports?: AgentV2ReferenceSectionSupport[] }) {
  return (
    <div className="text-sm font-normal leading-5 text-brand-neutral-950 compatible-word-break">
      <Link href={source.url} external>
        <AgentV2WebSourceIcon url={source.url} className="mr-1 h-4 w-4" />
        {source.title}
        <span className="sr-only"> (opens in a new tab)</span>
      </Link>
      <p className="mt-1 text-xs text-brand-neutral-950">{new URL(source.url).hostname} • web source</p>
      {supports && supports.length > 0 && (
        <>
          <div className="mt-2.5 flex items-center gap-1 text-xs font-semibold leading-4 text-brand-neutral-700">
            <Sparkles aria-hidden="true" className="h-3.5 w-3.5" />
            <span>Summary</span>
          </div>
          <ul className="mt-1 space-y-2 list-none text-xs leading-4 text-brand-neutral-800">
            {supports.map((support) => (
              <li key={support.reference.citation_id}>{support.reference.support.summary}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

/** Match other citation types: hover/focus previews; click opens the source. */
export function AgentV2WebSourceTooltip({
  source,
  supports,
  children,
}: {
  source: WebSource
  supports?: AgentV2ReferenceSectionSupport[]
  children: ReactElement
}) {
  // Same line-fragment anchoring as AgentV2CitationTooltip: a citation that wraps
  // across lines anchors the card to the hovered line, not the union of both lines.
  const anchorStateRef = useRef<{ element: HTMLElement | null; pointerY: number | null }>({ element: null, pointerY: null })
  const fragmentAnchor = useMemo(
    () => ({
      getBoundingClientRect: () => {
        const { element, pointerY } = anchorStateRef.current
        return element ? pickCitationAnchorRect(element, pointerY) : new DOMRect()
      },
      get contextElement() {
        return anchorStateRef.current.element ?? undefined
      },
    }),
    []
  )

  const recordPointer = (event: MouseEvent<HTMLElement>) => {
    anchorStateRef.current = { element: event.currentTarget, pointerY: event.clientY }
  }
  const trigger = cloneElement(children, {
    onMouseOver: (event: MouseEvent<HTMLElement>) => {
      children.props.onMouseOver?.(event)
      recordPointer(event)
    },
    onMouseMove: (event: MouseEvent<HTMLElement>) => {
      children.props.onMouseMove?.(event)
      recordPointer(event)
    },
    onFocus: (event: FocusEvent<HTMLElement>) => {
      children.props.onFocus?.(event)
      anchorStateRef.current = { element: event.currentTarget, pointerY: null }
    },
  })

  return (
    <CustomizableTooltip
      arrow
      describeChild
      enterDelay={1000}
      enterNextDelay={1000}
      disableTouchListener
      offset={[0, 0]}
      flipAltBoundary
      popperAnchorEl={fragmentAnchor}
      tooltipClassName="!max-w-[min(460px,calc(100vw-2rem))] !p-5 !bg-white !rounded-md !text-brand-neutral-950 !font-sans border border-brand-neutral-200 shadow-[0_0.6px_25px_0_rgba(0,0,0,0.10),0_10px_10px_0_rgba(0,0,0,0.04)]"
      arrowClassName="!text-white !w-4 !h-2 [&::before]:border [&::before]:border-brand-neutral-200"
      title={<AgentV2WebSourceContent source={source} supports={supports} />}
    >
      {trigger}
    </CustomizableTooltip>
  )
}
